import {PolymerElement} from '@polymer/polymer';
import {ScopeBinding} from '../behaviors/keli-binding-behavior';

class KeliDiscussionController extends ScopeBinding(PolymerElement) {
  static get is() {
    return 'keli-discussion-controller'
  }

  static get properties() {
    return {
      dataLoaded: {type: Boolean, value: false},
      element: {type: Object},
      focusElement: {type: Boolean, scopevar: 'focus', observer: 'focusElementChanged'},
      readOnly: {type: Boolean, scopevar: 'ro', observer: 'readOnlyChanged'},
      reverse: {type: Boolean, scopevar: true, observer: 'reverseChanged'},
      rowIndex: {
        type: Object, value: () => {
          return {};
        }
      },
      sendOnClose: {type: Boolean, scopevar: true},
      textCasing: {type: String, observer: 'textCasingChanged', scopevar: true},
      unsavedText: {type: String, observer: 'unsavedTextChanged'}
    }
  }

  constructor() {
    super();
    this.addOnKeli({
      'uireloaddata': 'handleReloadData',
      'uiupdatepending': 'handleUpdatePending',
      'uierrorsavingdiscussion': 'handleErrorSavingDiscussion'
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.element = null;
  }

  async elementReady(element) {
    this.element = element;
    this.element.reverse = this.reverse;
    this.element.readOnly = this.readOnly;
    this.element.textCasing = this.textCasing;
    this.focusElementChanged(this.focusElement);

    this.unsavedTextChanged = throttle(async () => {
      if (this.unsavedText !== undefined) {
        this.element.unsavedChanges = false;
        await this.runKeliFunction('updateMessage', this.unsavedText);
      }
    }, 15000);

    this.executeOnKeliReadyComplete(async () => {
      await this.runKeliFunction('uiSet', 'mode', 'browse');
      this.loadMessages(null, true);
      this.loadEditMessage();
    });
  }

  focusElementChanged(newValue) {
    if (this.element && newValue && !KeliInterface.isTouchDevice) {
      this.element.dispatchEvent(new CustomEvent('focusElement', {detail: {element: this.element}}));
    }
  }

  reverseChanged(newValue) {
    if (this.element) {
      this.element.reverse = newValue;
    }
  }

  textCasingChanged(newValue) {
    if (this.element) {
      this.element.textCasing = newValue;
    }
  }

  readOnlyChanged(newValue) {
    if (this.element) {
      this.element.readOnly = newValue;
    }
  }

  async handleErrorSavingDiscussion(event) {
    if (event?.message) {
      const existingMessage = this.element.$.input.currentValue;
      const concatenatedMessage = existingMessage ? (`${event.message}\n${existingMessage}`) : event.message;

      this.element.$.input.currentValue = concatenatedMessage;
      this.element.submitObject.message = concatenatedMessage;
      this.element.updateInputHeight();
    }
  }

  async handleUpdatePending(event, templateInstance) {
    if (!event || event.message == null) {
      return;
    }
    if (this.element.unsavedChanges) {
      keliUILogger('unsavedChanges! Skipping update from server.');
      return;
    }
    if (event.clear) {
      templateInstance = this.getKeliTemplateInstance(templateInstance);
      await this.runKeliFunctionIn(templateInstance, 'setupNewMessage');
    }

    const message = event.message;
    this.element.$.input.currentValue = message;
    this.element.submitObject.message = message;
    this.element.updateInputHeight();
  }

  async handleReloadData(event, templateInstance) {
    await this.loadMessages(templateInstance);
  }

  async loadEditMessage() {
    if (this.readOnly) {
      return;
    }
    const messageRowObj = await this.runKeliFunction('setupEditMessage');
    if (messageRowObj?.message) {
      this.element.$.input.currentValue = messageRowObj.message;
      this.element.submitObject.message = messageRowObj.message;
      this.element.updateInputHeight();
    }
  }

  async loadMessages(templateInstance, isInitialLoad) {
    if (!this.isConnected) return;

    templateInstance = this.getKeliTemplateInstance(templateInstance);

    const newMessages = [];
    const existingMessageIDs = [];
    const controller = this;
    try {
      await this.runKeliFunctionIn(templateInstance, 'supercede', {
        domain: controller.element.keliname,
        func: async function () {
          const rowsObject = await this.get('rows');
          await this.checkSuperceded();
          if (!controller.isConnected) return;

          const rowsPage = await this.rowsGetPage(rowsObject, 0, 0, {
            idsonly: true,
            consolidate: true,
            operation: isInitialLoad ? 'open' : undefined,
            reload: false,
            loadallrows: true
          });
          await this.checkSuperceded();
          if (!controller.isConnected) return;

          const contextId = await this.get('_c');
          const templatePath = await this.get('_t.path');

          for (const rowNumber of Object.keys(rowsPage)) {
            const rowId = rowsPage[rowNumber];
            await this.checkSuperceded();
            let messageSent = await this.get(`rows.${rowId}.sent_server`);
            if (!messageSent) {
              messageSent = await this.get(`rows.${rowId}.sent`);
            }
            if (messageSent == null) {
              continue;
            }

            const messageId = await this.get(`rows.${rowId}.id`);
            const messageText = await this.get(`rows.${rowId}.message`);
            let systemMessage = await this.get(`rows.${rowId}.system_message`);
            if (typeof systemMessage === 'string' && ['t', 'T', 'y', 'Y'].includes(systemMessage)) {
              systemMessage = true;
            } else if (typeof systemMessage !== 'boolean') {
              systemMessage = false;
            }

            let showEdit = await this.showUserEditAction(rowId);
            await this.checkSuperceded();
            if (!controller.isConnected) return;

            if (!controller.rowIndex[messageId]) {
              const messageElement = controller.element.createNewMessage({
                id: messageId,
                editAddress: (showEdit ? `keli://${contextId}.${templatePath}/${await this.get(`rows.${rowId}.id`)}` : undefined),
                message: messageText,
                person: await this.get(`rows.${rowId}.created_user_id.name`),
                time: this.parseDateTime(messageSent),
                systemMessage
              });
              if (controller.reverse) {
                newMessages.unshift(messageElement);
              } else {
                newMessages.push(messageElement);
              }
            } else {
              controller.rowIndex[messageId].element.message = messageText;
            }

            existingMessageIDs.push(messageId);
          }
        }
      });
      if (!this.isConnected) return;

      // Delete any messages that don't exist anymore.
      const messages = this.element.$.messages.querySelectorAll('keli-discussion-message');
      for (let i = 0; i < messages.length; ++i) {
        if (!messages[i].id || !existingMessageIDs.includes(messages[i].id)) {
          this.element.$.messages.removeChild(messages[i]);
          delete this.rowIndex[messages[i].id];
        }
      }

      // Add messages to the DOM, if they don't already exist.
      for (let i = 0; i < newMessages.length; ++i) {
        const existingIDs = Object.keys(this.rowIndex);
        if (!existingIDs.includes(newMessages[i].id)) {
          this.rowIndex[newMessages[i].id] = {element: newMessages[i]};
          if (this.reverse) {
            this.element.$.messages.insertBefore(newMessages[i], this.element.$.messages.childNodes[0]);
          } else {
            this.element.$.messages.appendChild(newMessages[i]);
          }
        }
      }

      if (!this.dataLoaded) {
        this.dataLoaded = true;
        this.dispatchEvent(new CustomEvent('dataLoaded', {
          bubbles: true,
          composed: true,
          detail: {element: this.element}
        }));
      }
    } catch (error) {
      if (!error.superceded) {
        console.error(error);
      }
    }
  }
}

customElements.define(KeliDiscussionController.is, KeliDiscussionController);
export {KeliDiscussionController};
